import { utc, UTC, isNullOrUndefined } from 'rewire-common';
import {
  createWatcherFn,
  WatcherTypeFn,
  DataSignal,
  property,
  version
} from 'rewire-core';
import { arraySearch, IFieldDefns } from 'rewire-ui';
import { IUdf, UdfDocumentType, UdfType } from '../DataTypes';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';
import { createUdfSearch } from '../utility/Searchers';
import FilterDialogModel from '../utility/FilterDialogModel';

export default abstract class UdfsFilterDialogModel<TState = unknown> extends FilterDialogModel<TState> {
  protected watchUdfs: WatcherTypeFn;
  protected _udfs: DataSignal<IUdf[]>;

  protected constructor(title: string, protected documentType?: UdfDocumentType, protected allowableTypes: UdfType[] = ['boolean', 'reference', 'text'], protected booleanFieldType: 'checkbox' | 'select' = 'select') {
    super(title);

    this._udfs = property([] as IUdf[]);
    this.watchUdfs = createWatcherFn();
    this.calculateCurrentUdfs();
    this.watchUdfs(() => { version(udfsSubscriptionService.subscriptionModel.udfs); }, () => {
      this.calculateCurrentUdfs();
    });
  }

  set udfs(value: IUdf[]) {
    this.udfs.length = 0;
    this.udfs.push(...value);
  }

  get udfs(): IUdf[] {
    return this._udfs();
  }

  calculateCurrentUdfs() {
    const documentTypeFilter = this.documentType ? [this.documentType] : undefined;
    const currUdfs = udfsSubscriptionService.subscriptionModel.getUdfs({ documentType: documentTypeFilter, type: this.allowableTypes });
    this.calculateFormUdfs(currUdfs, this.udfs);
    this.udfs = currUdfs;
  }

  calculateFormUdfs(currUdfs: IUdf[], oldUdfs: IUdf[]) {
    const toAdd = [...currUdfs];
    if (oldUdfs?.length) {
      const toRemove = [] as string[];
      for (const oldUdf of oldUdfs) {
        const idx = toAdd.findIndex((udf) => (udf.id === oldUdf.id) && (udf.displayName === oldUdf.displayName) && (udf.allowableValues === oldUdf.allowableValues));
        if (idx >= 0) {
          toAdd.splice(idx, 1);
        } else {
          toRemove.push(oldUdf.name);
        }
      }
      this.form.removeFields(toRemove);
    }

    if (!toAdd.length) return;
    this.addUdfFormFields(toAdd);
  }

  protected mapToForm(obj: ObjectType): ObjectType {
    const filterFormFieldValues = { ...obj } as ObjectType;
    if (filterFormFieldValues.custom) {
      if (filterFormFieldValues.custom.length) {
        filterFormFieldValues['fields'] = {};
        for (const value of filterFormFieldValues.custom) {
          filterFormFieldValues['fields'][value.field] = value.in ?? value.eq;
        }
      }
      delete filterFormFieldValues.custom;
    }
    return filterFormFieldValues;
  }

  protected mapToSave(): ObjectType {
    const newFilters = { ...this.form.toObjectValues() };
    if (newFilters.fields) {
      newFilters.custom = [];
      for (const key of Object.keys(newFilters.fields)) {
        const value = newFilters.fields[key];
        if (isNullOrUndefined(value) || (Array.isArray(value) && !value.length)) continue;
        const fieldFilter = {
          field: key,
          in: value,
        };
        newFilters.custom.push(fieldFilter);
      }
      delete newFilters.fields;
    }
    for (const key of Object.keys(newFilters)) {
      if (isNullOrUndefined(newFilters[key]) || (Array.isArray(newFilters[key]) && !newFilters[key].length)) {
        delete newFilters[key];
      }
    }
    return newFilters;
  }

  addUdfFormFields(udfs: IUdf[]) {
    this.form.addFields((_) => {
      const fields: IFieldDefns = {};
      const boolSearcher = arraySearch([true, false], (item: any) => !isNullOrUndefined(item) ? (item ? 'Yes' : 'No') : '');
      for (const udf of udfs) {
        switch (udf.type) {
          case 'date':
            fields[udf.name] = _.date().accessor(['fields', udf.name]).label(udf.displayName).placeholder(`Select ${udf.displayName}`);
            fields[udf.name] = _.date().accessor((obj) => obj?.fields?.[udf.name], (obj, value) => {
              if (!obj) return;
              if (!obj.fields) obj.fields = {};
              const newValue = value && (value instanceof UTC ? value : utc(value.toString()));
              if (newValue) {
                obj.fields[udf.name] = newValue;
              } else {
                delete obj.fields[udf.name];
              }
            }).label(udf.displayName).placeholder(`Select ${udf.displayName}`);
            break;
          case 'float':
            fields[udf.name] = _.number({ thousandSeparator: true, decimals: 4 }).accessor(['fields', udf.name]).label(udf.displayName).placeholder(`Select ${udf.displayName}`);
            break;
          case 'integer':
            fields[udf.name] = _.number({ thousandSeparator: true, decimals: 0 }).accessor(['fields', udf.name]).label(udf.displayName).placeholder(`Select ${udf.displayName}`);
            break;
          case 'boolean':
            fields[udf.name] = this.booleanFieldType === 'select'
              ? _.select(boolSearcher).accessor(['fields', udf.name]).label(udf.displayName).placeholder(`Select ${udf.displayName}`)
              : _.boolean().accessor(['fields', udf.name]).label(udf.displayName).placeholder(`Select ${udf.displayName}`);
            break;
          case 'text':
            if (udf.allowableValues) {
              const textSearcher = createUdfSearch(udf, (v) => v.name);
              fields[udf.name] = _.multiselectautocomplete(textSearcher, { chipLimit: 2 }).accessor(['fields', udf.name]).label(udf.displayName).placeholder(`Select ${udf.displayName}`);
            } else {
              const search = arraySearch([], (v) => Array.isArray(v) ? '' : (v || ''));
              const inputAdd = ((v: string) => v);
              fields[udf.name] = _.multiselectautocomplete(search, { chipLimit: 2, inputAdd: inputAdd }).accessor(['fields', udf.name]).label(udf.displayName).placeholder(`Select ${udf.displayName}`);
            }
            break;
          case 'reference': {
            const refSearcher = createUdfSearch(udf);
            fields[udf.name] = _.multiselectautocomplete(refSearcher, { chipLimit: 2 }).accessor(['fields', udf.name]).label(udf.displayName).placeholder(`Select ${udf.displayName}`);
            break;
          }
        }
      }
      return fields;
    });
  }
}